import React from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import EPDRow from "./EPDRow";
import EPDOverview from "./EPDOverview";
import { epdSearchStore } from "store/Mapping/EPDSearchStore";
import {
  Material2050SearchOUT,
  PaginatedMaterial2050SearchOUT,
} from "api-client";
import LoadingView from "./LoadingView";
import EmptyView from "./EmptyView";
import { observer } from "mobx-react-lite";

export interface OverviewProps {
  state: boolean;
  epd: Material2050SearchOUT | null;
}

export interface LoadingState {
  id: string;
  state: boolean;
}

const PAGE_SIZE = 50;

const SearchTable = (props: {
  term: string;
  datasetIndex: number;
  rowIndex: number;
  close: () => void;
}) => {
  const { t } = useTranslation();
  const [showOverview, setShowOverview] = React.useState<OverviewProps>({
    state: false,
    epd: null,
  });
  const [EPDs, setEPDs] = React.useState<PaginatedMaterial2050SearchOUT>({
    count: 0,
    items: [],
  });
  const [hasMoreOptions, setHasMoreOptions] = React.useState(false);
  const [loadingEPDs, setLoadingEPDs] = React.useState(true);
  const [loadingEPDSelect, setLoadingEPDSelect] = React.useState<LoadingState>({
    id: "",
    state: false,
  });
  const [error, setError] = React.useState<number>();

  React.useEffect(() => {
    getEPDs(1, true);
    setShowOverview({ state: false, epd: null });
  }, [props.term, epdSearchStore.selectedFilters]);

  async function getEPDs(pageNumber?: number, update?: boolean) {
    setLoadingEPDs(true);
    if (update) setEPDs({ count: 0, items: [] });
    closeOverview();
    try {
      const response = await epdSearchStore.fetch2050EPDs(
        props.term,
        pageNumber ?? 1,
        PAGE_SIZE
      );
      if (!response) return;
      if (update || !EPDs.items?.length) {
        setEPDs(response);
      } else if (EPDs.items?.length && response.items?.length) {
        setEPDs({
          count: response.count,
          items: [...EPDs.items, ...response.items],
        });
      }
      setHasMoreOptions(EPDs?.items?.length !== response.count);
      setError(undefined);
    } catch (error) {
      if (error && typeof error === "object" && "status" in error) {
        setError((error as { status: number }).status);
      }
    } finally {
      setLoadingEPDs(false);
    }
  }

  async function loadMoreEPDs(e: React.UIEvent<HTMLUListElement, UIEvent>) {
    e.stopPropagation();
    const reachedToEnd = EPDs?.items?.length === EPDs?.count;
    if (!hasMoreOptions || loadingEPDs || reachedToEnd) return;
    if (e.target) {
      const { scrollHeight, scrollTop, clientHeight } =
        e.target as HTMLDivElement;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        await getEPDs(Number(EPDs?.items?.length) / PAGE_SIZE + 1, false);
      }
    }
  }

  const openOverview = (epd: Material2050SearchOUT) => {
    setShowOverview({ state: true, epd: epd });
  };

  const handleSelectEPD = async (epd_id: string) => {
    setLoadingEPDSelect({ id: epd_id, state: true });
    const res = await epdSearchStore.handleSelect2050EPD(
      epd_id,
      props.datasetIndex,
      props.rowIndex
    );
    setLoadingEPDSelect({ id: "", state: false });
    res && props.close();
  };

  function tableFooter() {
    return (
      <div
        className={clsx(
          "bg-gray-50 text-gray-500 px-4 py-2 h-8 flex items-center border-t border-gray-300",
          "text-xs font-medium"
        )}
      >
        {t("productEditor.loadedEPDCount", {
          count: EPDs?.items?.length,
          total: EPDs?.count,
        })}
      </div>
    );
  }

  const closeOverview = () => setShowOverview({ state: false, epd: null });

  return (
    <div className="grid grid-cols-2 border border-gray-300 rounded-md mt-2 overflow-hidden">
      <div
        className={clsx("col-span-1", {
          "col-span-2": !showOverview.state,
          "border-r border-gray-300": showOverview.state,
        })}
      >
        {loadingEPDs && EPDs?.items?.length === 0 ? (
          <LoadingView
            className="!h-20 !w-20"
            message={t("productEditor.EPDWereLoaded")}
          />
        ) : (
          <div className="w-full relative">
            <ul
              className={clsx("h-[calc(100vh-270px)] overflow-y-auto", {
                "h-[calc(100vh-322px)]": loadingEPDs && EPDs?.items?.length,
              })}
              onScroll={loadMoreEPDs}
            >
              {EPDs?.items?.length ? (
                EPDs?.items?.map((item, index) => (
                  <EPDRow
                    epd={item}
                    key={index}
                    showOverview={showOverview}
                    setOpenOverview={openOverview}
                    handleSelectEPD={handleSelectEPD}
                    loadingEPDSelect={loadingEPDSelect}
                  />
                ))
              ) : (
                <EmptyView
                  errorCode={error}
                  message={t("productEditor.technicalErrorMessage")}
                />
              )}
            </ul>
            {loadingEPDs && EPDs?.items?.length && (
              <div
                className={clsx(
                  "border-t bottom-8 bg-white z-30 w-full h-[52px] flex justify-center items-center"
                )}
              >
                <LoadingView
                  className="mr-2 !h-4 !w-4"
                  message={t("productEditor.furtherEPDWereLoaded")}
                  containerClassName="!flex-row"
                  messageClassName="!mt-0"
                />
              </div>
            )}
            {EPDs.items?.length ? tableFooter() : null}
          </div>
        )}
      </div>
      {showOverview.state && (
        <EPDOverview
          epd={showOverview.epd}
          close={closeOverview}
          handleSelectEPD={handleSelectEPD}
          loadingEPDSelect={loadingEPDSelect}
        />
      )}
    </div>
  );
};

export default observer(SearchTable);
